import { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";

const DISMISS_KEY = "jju_install_dismissed_at";
const DISMISS_DAYS = 14;

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * "Install Jesus Just Us on your phone" banner.
 * - Android / Chrome: catches the beforeinstallprompt event and shows a native-feeling Install button.
 * - iOS Safari: shows a friendly hint explaining the Share → Add to Home Screen steps.
 * - Already installed (standalone) or dismissed within DISMISS_DAYS: hidden.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [iosVisible, setIosVisible] = useState(false);
  const [androidVisible, setAndroidVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Wait a beat so we don't ambush a fresh visitor on the landing page hero.
      setTimeout(() => setAndroidVisible(true), 4000);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari never fires beforeinstallprompt — show our own gentle hint.
    if (isiOS()) {
      const t = setTimeout(() => setIosVisible(true), 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setIosVisible(false);
    setAndroidVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* noop */ }
    setDeferred(null);
    setAndroidVisible(false);
  };

  if (!iosVisible && !androidVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 px-4 pointer-events-none" data-testid="install-prompt">
      <div className="max-w-md mx-auto pointer-events-auto jj-card p-4 bg-cream shadow-2xl border border-burgundy/20 fade-up">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-burgundy text-cream flex items-center justify-center shrink-0">
            <Download className="h-5 w-5" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-jjblue text-lg leading-tight">Install Jesus Just Us</div>
            {androidVisible ? (
              <p className="mt-1 text-xs text-[var(--jj-text-secondary)] leading-relaxed">
                Add JJU to your phone for a full-screen, app-like experience. Stays signed in. One tap from your home screen.
              </p>
            ) : (
              <p className="mt-1 text-xs text-[var(--jj-text-secondary)] leading-relaxed">
                Tap <Share className="inline h-3 w-3 -mt-0.5" /> <span className="font-medium">Share</span>, then{" "}
                <Plus className="inline h-3 w-3 -mt-0.5" /> <span className="font-medium">Add to Home Screen</span> to install JJU on your iPhone.
              </p>
            )}
            <div className="mt-3 flex items-center gap-2">
              {androidVisible && (
                <button
                  onClick={install}
                  className="text-xs bg-burgundy text-cream px-3 py-1.5 rounded-full font-medium hover:bg-[var(--jj-burgundy-hover)]"
                  data-testid="install-prompt-install"
                >
                  Install
                </button>
              )}
              <button
                onClick={dismiss}
                className="text-xs text-[var(--jj-muted)] hover:text-burgundy px-2 py-1"
                data-testid="install-prompt-dismiss"
              >
                {androidVisible ? "Not now" : "Got it"}
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-[var(--jj-muted)] hover:text-burgundy -mt-1 -mr-1 p-1"
            aria-label="Dismiss"
            data-testid="install-prompt-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
